// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  Add as AddIcon,
  SwapHoriz as SwapHorizIcon,
} from '@/constants/hardfin-standard-icons';
import { Sort, SortDirection, sortDirectionSchema } from '@/constants/sort';
import { getItem } from '@/items/api/get-item';
import { DEFAULT_MOVEMENT_PAGE_SIZE } from '@/items/api/get-item-movement-history';
import { AdjustInventoryDialog } from '@/items/components/adjust-inventory-dialog';
import { AdjustInventoryProvider } from '@/items/components/adjust-inventory-provider';
import {
  MovementHistoryTable,
  MovementHistoryTableSkeleton,
} from '@/items/components/movement-history-table';
import { TransferInventoryDialog } from '@/items/components/transfer-inventory-dialog';
import { TransferInventoryProvider } from '@/items/components/transfer-inventory-provider';
import { useAdjustInventory } from '@/items/hooks/use-adjust-inventory';
import { useItemMovementHistory } from '@/items/hooks/use-item-movement-history';
import { useTransferInventory } from '@/items/hooks/use-transfer-inventory';
import type { SparseItem } from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { Button, Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense } from 'react';
import { z } from 'zod';

/**
 * Search params schema for the movement history tab.
 */
const movementSearchSchema = z.object({
  page: z.number().optional().default(1),
  sortOrder: sortDirectionSchema.optional().default('desc'),
});

export const Route = createFileRoute('/_app/catalog/items/$itemKey/movement')({
  component: ItemMovementRouteWithDialogs,
  validateSearch: movementSearchSchema,
});

function ItemMovementRouteWithDialogs() {
  return (
    <AdjustInventoryProvider>
      <TransferInventoryProvider>
        <ItemMovementRouteContent />
      </TransferInventoryProvider>
    </AdjustInventoryProvider>
  );
}

function ItemMovementRouteContent() {
  const { itemKey } = Route.useParams();
  const { page, sortOrder } = Route.useSearch();

  const { data: itemData } = useSuspenseQuery({
    queryKey: ['item', itemKey],
    queryFn: () => getItem(itemKey),
  });

  // Check if this is a bulk item (isPhysical && !hasAssets)
  const isBulkItem = itemData.item.isPhysical && !itemData.item.hasAssets;

  // Redirect if not a bulk item
  if (!isBulkItem) {
    return (
      <Navigate to="/catalog/items/$itemKey" params={{ itemKey }} replace />
    );
  }

  return (
    <BulkItemMovementContent
      itemKey={itemKey}
      page={page}
      sortOrder={sortOrder}
      sparseItem={extractSparseItem(itemData.item)}
    />
  );
}

interface BulkItemMovementContentProps {
  itemKey: string;
  page: number;
  sortOrder: SortDirection;
  sparseItem: SparseItem;
}

function BulkItemMovementContent(props: BulkItemMovementContentProps) {
  const {
    isOpen: isAdjustOpen,
    item: adjustItem,
    location: adjustLocation,
    openDialog: openAdjustDialog,
    closeDialog: closeAdjustDialog,
  } = useAdjustInventory();

  const {
    isOpen: isTransferOpen,
    item: transferItem,
    location: transferLocation,
    openDialog: openTransferDialog,
    closeDialog: closeTransferDialog,
  } = useTransferInventory();

  return (
    <>
      <HardfinTabCardWrapper
        title="Movement history"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() =>
                openAdjustDialog({
                  item: props.sparseItem,
                  location: undefined,
                })
              }
            >
              Adjust
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHorizIcon fontSize="small" />}
              onClick={() =>
                openTransferDialog({
                  item: props.sparseItem,
                  location: undefined,
                })
              }
            >
              Transfer
            </Button>
          </Stack>
        }
      >
        <Stack padding={2}>
          <SupportErrorBoundary message="Failed to load movement history">
            <Suspense fallback={<MovementHistoryTableSkeleton />}>
              <MovementHistoryTableContainer
                itemKey={props.itemKey}
                page={props.page}
                sortOrder={props.sortOrder}
              />
            </Suspense>
          </SupportErrorBoundary>
        </Stack>
      </HardfinTabCardWrapper>
      <AdjustInventoryDialog
        isOpen={isAdjustOpen}
        item={adjustItem}
        location={adjustLocation}
        onClose={closeAdjustDialog}
      />
      <TransferInventoryDialog
        isOpen={isTransferOpen}
        item={transferItem}
        location={transferLocation}
        onClose={closeTransferDialog}
      />
    </>
  );
}

interface MovementHistoryTableContainerProps {
  itemKey: string;
  page: number;
  sortOrder: SortDirection;
}

function MovementHistoryTableContainer(
  props: MovementHistoryTableContainerProps
) {
  const navigate = useNavigate({ from: Route.fullPath });
  const { movements, totalCount, isFetching } = useItemMovementHistory({
    itemKey: props.itemKey,
    page: props.page,
    limit: DEFAULT_MOVEMENT_PAGE_SIZE,
    sortOrder: props.sortOrder,
  });

  const sort: Sort<'movedAt'> = {
    property: 'movedAt',
    direction: props.sortOrder,
  };

  const handlePageChange = (newPage: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const handleSortChange = (newSort: Sort<'movedAt'>) => {
    navigate({
      search: (prev) => ({ ...prev, sortOrder: newSort.direction, page: 1 }),
    });
  };

  return (
    <MovementHistoryTable
      movements={movements}
      totalCount={totalCount}
      page={props.page}
      pageSize={DEFAULT_MOVEMENT_PAGE_SIZE}
      sort={sort}
      isFetching={isFetching}
      onPageChange={handlePageChange}
      onSortChange={handleSortChange}
    />
  );
}
